import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Typography,
  Modal,
  Fade,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import {
  getAirlineCarryOnListAPI,
  saveAirlineCarryOnAPI,
  updateAirlineCarryOnAPI,
} from '../operations/airlineCarryOnService';

/* ================= API =================
 * Routes through the shared gateway client (operations/apiClient.js +
 * environment.js): api-* /app/v1/beadmin/airline-carryon*, AES-encrypted body /
 * response + JWT, same as the supplier-config / service-fee pages. The gateway
 * forwards to the engine path /be/v1/admin/airline-carryon*.
 */

/* ================= TYPES ================= */
interface CarryOnBaggage {
  KG: string;
  LB: string;
}

interface AirlineCarryOn {
  airlineCode: string;
  airlineName: string;
  carryOnBaggage: CarryOnBaggage;
  createdAt?: string;
  updatedAt?: string;
}

/* ================= VALIDATION HELPERS ================= */
// 2 alphanumeric characters (stored uppercase)
const CODE_REGEX = /^[A-Za-z0-9]{2}$/;
// e.g. "10KG", "7.5KG" or the placeholder "-"
const KG_REGEX = /^(\d+(\.\d+)?KG|-)$/;
// e.g. "22.05LB", "15LB" or the placeholder "-"
const LB_REGEX = /^(\d+(\.\d+)?LB|-)$/;

// Auto-convert a KG entry to its LB equivalent (1 kg = 2.20462262 lb),
// matching the stored format e.g. "10KG" -> "22.05LB", "-" -> "-".
const KG_TO_LB = 2.20462262;
const kgToLb = (kg: string): string => {
  const v = (kg ?? '').trim();
  if (v === '') return '';
  if (v === '-') return '-';
  const num = parseFloat(v.replace(/kg/i, '').replace(/[^0-9.]/g, ''));
  if (!isFinite(num)) return '';
  return `${(num * KG_TO_LB).toFixed(2)}LB`;
};

const emptyForm = (): AirlineCarryOn => ({
  airlineCode: '',
  airlineName: '',
  carryOnBaggage: { KG: '', LB: '' },
});

const formatDate = (value?: string) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const AirlineCarryOnBaggage = () => {
  const [rows, setRows] = useState<AirlineCarryOn[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [newRecord, setNewRecord] = useState<AirlineCarryOn>(emptyForm());
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<AirlineCarryOn | null>(null);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMsg, setSnackbarMsg] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('success');

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning' = 'success'
  ) => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Pull a human-readable message out of a backend error envelope
  const extractApiError = (err: any, fallback: string): string => {
    const data = err?.response?.data;
    if (data?.data?.validationErrors?.length) {
      return data.data.validationErrors.join(', ');
    }
    if (data?.message) return data.message;
    return fallback;
  };

  /* ================= FETCH LIST ================= */
  const fetchCarryOnList = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const collected: AirlineCarryOn[] = [];
      let lastKey: string | null = null;

      // Reference data (~792 rows). Page through until lastKey is null.
      do {
        const params: Record<string, any> = { limit: 1000 };
        if (lastKey) params.lastKey = lastKey;

        // apiClient returns the decrypted envelope: { status, message, data:{ data, lastKey, count } }
        const response = await getAirlineCarryOnListAPI(params);

        const payload = response?.data ?? {};
        const pageRows: any[] = Array.isArray(payload?.data) ? payload.data : [];

        pageRows.forEach((item) => {
          collected.push({
            airlineCode: item.airlineCode ?? '',
            airlineName: item.airlineName ?? '',
            carryOnBaggage: {
              KG: item?.carryOnBaggage?.KG ?? '-',
              LB: item?.carryOnBaggage?.LB ?? '-',
            },
            createdAt: item.createdAt ?? '',
            updatedAt: item.updatedAt ?? '',
          });
        });

        lastKey = payload?.lastKey ?? null;
      } while (lastKey);

      setRows(collected);
    } catch (err) {
      console.error('Failed to fetch airline carry-on list:', err);
      setLoadError('Failed to load airline carry-on list. Please try again.');
      showSnackbar('Failed to fetch airline carry-on list', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarryOnList();
  }, []);

  /* ================= PAGINATION ================= */
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  /* ================= SEARCH ================= */
  const filteredRows = rows.filter((row) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      row.airlineCode.toLowerCase().includes(term) ||
      row.airlineName.toLowerCase().includes(term)
    );
  });

  /* ================= CREATE ================= */
  const handleCreateOpen = () => {
    setNewRecord(emptyForm());
    setCreateErrors({});
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
    setCreateErrors({});
    setNewRecord(emptyForm());
  };

  const validateCreate = (record: AirlineCarryOn): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!CODE_REGEX.test(record.airlineCode)) {
      errors.airlineCode = 'Airline code must be exactly 2 alphanumeric characters';
    }
    if (!record.airlineName.trim()) {
      errors.airlineName = 'Airline name is required';
    }
    if (!KG_REGEX.test(record.carryOnBaggage.KG)) {
      errors.KG = 'KG must be like "10KG" / "7.5KG" or "-"';
    }
    if (!LB_REGEX.test(record.carryOnBaggage.LB)) {
      errors.LB = 'LB must be like "22.05LB" / "15LB" or "-"';
    }
    return errors;
  };

  const handleSaveNew = async () => {
    const errors = validateCreate(newRecord);
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      showSnackbar('Please fix the highlighted fields', 'warning');
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        airlineCode: newRecord.airlineCode.toUpperCase(),
        airlineName: newRecord.airlineName.trim(),
        carryOnBaggage: {
          KG: newRecord.carryOnBaggage.KG,
          LB: newRecord.carryOnBaggage.LB,
        },
      };

      await saveAirlineCarryOnAPI(payload);

      showSnackbar('Airline carry-on record created successfully', 'success');
      handleCreateClose();
      await fetchCarryOnList();
    } catch (err: any) {
      console.error('Failed to create airline carry-on record:', err);
      const status = err?.response?.status;
      if (status === 409) {
        showSnackbar(
          extractApiError(
            err,
            `Record already exists for code '${newRecord.airlineCode.toUpperCase()}'`
          ),
          'error'
        );
      } else {
        showSnackbar(extractApiError(err, 'Failed to create record'), 'error');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  /* ================= EDIT ================= */
  const handleEditOpen = (record: AirlineCarryOn) => {
    setEditRecord({
      ...record,
      carryOnBaggage: { ...record.carryOnBaggage },
    });
    setEditErrors({});
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditErrors({});
    setEditRecord(null);
  };

  const validateEdit = (record: AirlineCarryOn): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!KG_REGEX.test(record.carryOnBaggage.KG)) {
      errors.KG = 'KG must be like "10KG" / "7.5KG" or "-"';
    }
    if (!LB_REGEX.test(record.carryOnBaggage.LB)) {
      errors.LB = 'LB must be like "22.05LB" / "15LB" or "-"';
    }
    return errors;
  };

  const handleUpdate = async () => {
    if (!editRecord) return;

    const errors = validateEdit(editRecord);
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      showSnackbar('Please fix the highlighted fields', 'warning');
      return;
    }

    try {
      setSaveLoading(true);
      // airlineCode is taken from the URL only; airlineCode/airlineName in the
      // body would be rejected by the backend, so we send weights only.
      const payload = {
        carryOnBaggage: {
          KG: editRecord.carryOnBaggage.KG,
          LB: editRecord.carryOnBaggage.LB,
        },
      };

      await updateAirlineCarryOnAPI(editRecord.airlineCode, payload);

      showSnackbar('Airline carry-on record updated successfully', 'success');
      handleEditClose();
      await fetchCarryOnList();
    } catch (err: any) {
      console.error('Failed to update airline carry-on record:', err);
      const status = err?.response?.status;
      if (status === 404) {
        showSnackbar(
          extractApiError(
            err,
            `Record not found for code '${editRecord.airlineCode}'`
          ),
          'error'
        );
      } else {
        showSnackbar(extractApiError(err, 'Failed to update record'), 'error');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Box className="headingBtnWrap">
        <Typography variant="h1">Carry on Baggage</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateOpen}
          style={{
            backgroundColor: '#272727',
            color: '#fff',
            padding: '8px 14px',
            fontWeight: 'bold',
          }}
        >
          New
        </Button>
      </Box>

      {/* Search */}
      <Box className="searchInputWrap">
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          p={2}
          sx={{ width: '100%' }}
          className="searchInputFilterBox"
        >
          <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <SearchIcon
              sx={{
                position: 'absolute',
                left: 10,
                color: '#888',
                fontSize: 20,
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              className="inputField"
              placeholder="Search by airline code or name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              style={{ minWidth: 320, height: '38px', paddingLeft: 36 }}
            />
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="airline carry-on baggage table">
            <TableHead>
              <TableRow>
                <TableCell style={{ minWidth: 120 }}>Airline Code</TableCell>
                <TableCell style={{ minWidth: 200 }}>Airline Name</TableCell>
                <TableCell style={{ minWidth: 120 }}>Carry-On (KG)</TableCell>
                <TableCell style={{ minWidth: 120 }}>Carry-On (LB)</TableCell>
                <TableCell style={{ minWidth: 180 }}>Updated At</TableCell>
                <TableCell style={{ minWidth: 100 }} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                paginatedRows.map((row) => (
                  <TableRow hover key={row.airlineCode}>
                    <TableCell>{row.airlineCode}</TableCell>
                    <TableCell>{row.airlineName}</TableCell>
                    <TableCell>{row.carryOnBaggage.KG}</TableCell>
                    <TableCell>{row.carryOnBaggage.LB}</TableCell>
                    <TableCell>{formatDate(row.updatedAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEditOpen(row)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && loadError && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ mb: 2, color: 'error.main', fontWeight: 600 }}>
                      {loadError}
                    </Typography>
                    <Button variant="outlined" onClick={fetchCarryOnList}>
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              )}

              {!loading && !loadError && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 8, fontWeight: 600, color: 'text.secondary' }}
                  >
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Create Modal */}
      <Modal
        open={createOpen}
        onClose={handleCreateClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
        }}
      >
        <Fade in={createOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: '#f5f5f5',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxWidth: '420px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <CancelIcon
              onClick={handleCreateClose}
              className="closeModal"
              sx={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer', color: '#555' }}
            />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Add Carry on Baggage
            </Typography>

            <Box component="form" className="modalForm">
              <div className="form-control inputFieldWrap">
                <label>Airline Code</label>
                <input
                  type="text"
                  className="inputField"
                  maxLength={2}
                  value={newRecord.airlineCode}
                  onChange={(e) =>
                    setNewRecord((prev) => ({
                      ...prev,
                      airlineCode: e.target.value.toUpperCase(),
                    }))
                  }
                  style={createErrors.airlineCode ? { border: '1px solid red' } : {}}
                />
                {createErrors.airlineCode && (
                  <span className="errorNewSupplier">{createErrors.airlineCode}</span>
                )}
              </div>

              <div className="form-control inputFieldWrap">
                <label>Airline Name</label>
                <input
                  type="text"
                  className="inputField"
                  value={newRecord.airlineName}
                  onChange={(e) =>
                    setNewRecord((prev) => ({ ...prev, airlineName: e.target.value }))
                  }
                  style={createErrors.airlineName ? { border: '1px solid red' } : {}}
                />
                {createErrors.airlineName && (
                  <span className="errorNewSupplier">{createErrors.airlineName}</span>
                )}
              </div>

              <div className="form-control inputFieldWrap">
                <label>Carry-On (KG)</label>
                <input
                  type="text"
                  className="inputField"
                  placeholder='e.g. 10KG or -'
                  value={newRecord.carryOnBaggage.KG}
                  onChange={(e) => {
                    const kg = e.target.value.toUpperCase();
                    setNewRecord((prev) => ({
                      ...prev,
                      carryOnBaggage: { ...prev.carryOnBaggage, KG: kg, LB: kgToLb(kg) },
                    }));
                  }}
                  style={createErrors.KG ? { border: '1px solid red' } : {}}
                />
                {createErrors.KG && (
                  <span className="errorNewSupplier">{createErrors.KG}</span>
                )}
              </div>

              <div className="form-control inputFieldWrap">
                <label>Carry-On (LB)</label>
                <input
                  type="text"
                  className="inputField"
                  placeholder='e.g. 22.05LB or -'
                  value={newRecord.carryOnBaggage.LB}
                  onChange={(e) =>
                    setNewRecord((prev) => ({
                      ...prev,
                      carryOnBaggage: { ...prev.carryOnBaggage, LB: e.target.value.toUpperCase() },
                    }))
                  }
                  style={createErrors.LB ? { border: '1px solid red' } : {}}
                />
                {createErrors.LB && (
                  <span className="errorNewSupplier">{createErrors.LB}</span>
                )}
              </div>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleSaveNew}
                disabled={saveLoading}
                className="formBtn"
                sx={{
                  backgroundColor: '#272727',
                  '&:hover': { backgroundColor: '#1a1a1a' },
                  textTransform: 'none',
                }}
              >
                {saveLoading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        onClose={handleEditClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
        }}
      >
        <Fade in={editOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: '#f5f5f5',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxWidth: '420px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <CancelIcon
              onClick={handleEditClose}
              className="closeModal"
              sx={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer', color: '#555' }}
            />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Edit Carry on Baggage
            </Typography>

            {editRecord && (
              <>
                <Box component="form" className="modalForm">
                  {/* Airline Code - immutable */}
                  <div className="form-control inputFieldWrap">
                    <label>Airline Code</label>
                    <input
                      type="text"
                      className="inputField"
                      value={editRecord.airlineCode}
                      disabled
                      readOnly
                      style={{ backgroundColor: '#e9e9e9' }}
                    />
                  </div>

                  {/* Airline Name - immutable */}
                  <div className="form-control inputFieldWrap">
                    <label>Airline Name</label>
                    <input
                      type="text"
                      className="inputField"
                      value={editRecord.airlineName}
                      disabled
                      readOnly
                      style={{ backgroundColor: '#e9e9e9' }}
                    />
                  </div>

                  {/* KG - editable */}
                  <div className="form-control inputFieldWrap">
                    <label>Carry-On (KG)</label>
                    <input
                      type="text"
                      className="inputField"
                      placeholder='e.g. 10KG or -'
                      value={editRecord.carryOnBaggage.KG}
                      onChange={(e) => {
                        const kg = e.target.value.toUpperCase();
                        setEditRecord((prev) =>
                          prev
                            ? {
                                ...prev,
                                carryOnBaggage: {
                                  ...prev.carryOnBaggage,
                                  KG: kg,
                                  LB: kgToLb(kg),
                                },
                              }
                            : prev
                        );
                      }}
                      style={editErrors.KG ? { border: '1px solid red' } : {}}
                    />
                    {editErrors.KG && (
                      <span className="errorNewSupplier">{editErrors.KG}</span>
                    )}
                  </div>

                  {/* LB - editable */}
                  <div className="form-control inputFieldWrap">
                    <label>Carry-On (LB)</label>
                    <input
                      type="text"
                      className="inputField"
                      placeholder='e.g. 22.05LB or -'
                      value={editRecord.carryOnBaggage.LB}
                      onChange={(e) =>
                        setEditRecord((prev) =>
                          prev
                            ? {
                                ...prev,
                                carryOnBaggage: {
                                  ...prev.carryOnBaggage,
                                  LB: e.target.value.toUpperCase(),
                                },
                              }
                            : prev
                        )
                      }
                      style={editErrors.LB ? { border: '1px solid red' } : {}}
                    />
                    {editErrors.LB && (
                      <span className="errorNewSupplier">{editErrors.LB}</span>
                    )}
                  </div>
                </Box>

                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}
                >
                  <Button
                    variant="contained"
                    onClick={handleUpdate}
                    disabled={saveLoading}
                    className="formBtn"
                    sx={{
                      backgroundColor: '#272727',
                      '&:hover': { backgroundColor: '#1a1a1a' },
                      textTransform: 'none',
                    }}
                  >
                    {saveLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Update'
                    )}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50% !important',
          left: '50% !important',
          transform: 'translate(-50%, -50%) !important',
          '& .MuiSnackbarContent-root': {
            textAlign: 'center',
            minWidth: '200px',
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '1rem',
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AirlineCarryOnBaggage;
